import {
  SoapProblem,
} from '../../transcriptions/transcriptions.service';

const ASSESSMENT_LABELS = new Set([
  'status',
  'supporting evidence',
  'supporting findings',
  'clinical interpretation',
  'clinical interpretation reasoning',
  'clinical reasoning',
  'differential',
  'severity',
  'severity stage',
  'risk',
  'risk red flags',
  'red flags',
  'barriers',
  'risk assessment',
  'assessment',
  'summary',
  'summary of assessment',
  'summary of assessments',
  'summary of all assessments',
]);

const PLAN_LABELS = new Set([
  'medications',
  'diagnostics ordered',
  'diagnostics',
  'referrals',
  'therapies',
  'monitoring',
  'counseling education',
  'counseling',
  'education',
  'patient education',
  'safety',
  'follow-up',
  'follow up',
  'preventive care',
  'paperwork forms',
  'paperwork',
  'care coordination',
  'patient instructions',
  'plan',
]);

const SKIP_PLAN_LABELS = new Set(['patient instructions']);

/**
 * One Visits "Assessment and Plan" body: summary, then each problem as a
 * narrative Assessment plus unlabeled Plan bullets.
 */
export function formatCombinedAssessmentAndPlan(
  problems: SoapProblem[] | null | undefined,
  summary?: string | null,
  patientName?: string | null
): string {
  const list = problems ?? [];
  if (!list.length) {
    return (summary ?? '').trim();
  }
  const parts: string[] = [];
  const sum = (summary ?? '').trim() || synthesizeAssessmentSummary(list, patientName);
  if (sum) {
    parts.push(sum);
  }

  for (const problem of list) {
    const name = (problem.name ?? '').trim() || 'Problem';
    const assessment = flattenAssessmentNarrative(problem.assessment);
    const planItems = flattenPlanItems(problem.plan);
    const hasAssessment =
      !!assessment && assessment !== '—' && assessment !== '-' && !/^n\/a$/i.test(assessment);
    const block: string[] = [name];
    if (hasAssessment) {
      block.push(assessment);
    }
    if (planItems.length) {
      block.push('Plan:');
      for (const item of planItems) {
        block.push(`• ${item}`);
      }
    }
    if (block.length > 1) {
      parts.push(block.join('\n'));
    }
  }
  return parts.join('\n\n').trim();
}

export function synthesizeAssessmentSummary(
  problems: SoapProblem[] | null | undefined,
  patientName?: string | null
): string {
  const names = (problems ?? [])
    .map((p) => (p.name ?? '').trim())
    .filter((n) => n && !/^problem(\s+\d+)?$/i.test(n) && !/^clinical assessment$/i.test(n));
  if (!names.length) {
    return '';
  }
  const who = (patientName ?? '').trim() || 'The patient';
  if (names.length === 1) {
    return `${who} presents with ${names[0]}.`;
  }
  if (names.length === 2) {
    return `${who} presents with ${names[0]} and ${names[1]}.`;
  }
  return `${who} presents with ${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}.`;
}

/** Strip Status / Supporting evidence / etc. and join into a narrative. */
export function flattenAssessmentNarrative(text: string | null | undefined): string {
  if (!text?.trim()) {
    return '';
  }
  const paras: string[] = [];
  let current: string[] = [];
  const flush = (): void => {
    if (!current.length) {
      return;
    }
    paras.push(current.join(' ').replace(/\s+/g, ' ').trim());
    current = [];
  };

  for (const raw of text.replace(/\r/g, '').split('\n')) {
    const trimmed = raw.trim();
    if (!trimmed) {
      flush();
      continue;
    }
    const stripped = stripKnownLabel(stripBullet(trimmed), ASSESSMENT_LABELS);
    if (stripped.skip) {
      continue;
    }
    if (!stripped.content) {
      continue;
    }
    current.push(stripped.content);
  }
  flush();
  return paras.join('\n\n').trim();
}

export function flattenPlanItems(items: string[] | null | undefined): string[] {
  return (items ?? [])
    .map((item) => flattenPlanItem(item))
    .filter((item): item is string => !!item);
}

export function flattenPlanItem(item: string | null | undefined): string | null {
  if (!item?.trim()) {
    return null;
  }
  const stripped = stripKnownLabel(stripBullet(item.trim()), PLAN_LABELS);
  if (stripped.skip || !stripped.content) {
    return null;
  }
  if (stripped.labelKey && SKIP_PLAN_LABELS.has(stripped.labelKey)) {
    return null;
  }
  return stripped.content;
}

/** Flatten labels in numbered assessment section text (legacy editor / soapNormalize). */
export function flattenAssessmentSectionText(text: string | null | undefined): string {
  if (!text?.trim()) {
    return '';
  }
  const blocks = splitNumberedBlocks(text);
  if (!blocks.length) {
    return flattenAssessmentNarrative(text);
  }
  return blocks
    .map((b) => {
      const heading = `${b.number}. ${b.name}`;
      const body = flattenAssessmentNarrative(b.body);
      return body ? `${heading}\n${body}` : heading;
    })
    .join('\n\n')
    .trim();
}

/** Flatten plan category labels in numbered plan section text. */
export function flattenPlanSectionText(text: string | null | undefined): string {
  if (!text?.trim()) {
    return '';
  }
  const blocks = splitNumberedBlocks(text);
  if (!blocks.length) {
    return (text ?? '')
      .replace(/\r/g, '')
      .split('\n')
      .map((line) => {
        const flat = flattenPlanItem(line);
        return flat ? `• ${flat}` : '';
      })
      .filter(Boolean)
      .join('\n');
  }
  return blocks
    .map((b) => {
      const heading = `${b.number}. ${b.name}`;
      const items = b.body
        .split('\n')
        .map((l) => flattenPlanItem(l))
        .filter((item): item is string => !!item)
        .map((item) => `• ${item}`);
      return items.length ? `${heading}\n${items.join('\n')}` : heading;
    })
    .join('\n\n')
    .trim();
}

/**
 * Format a SoapProblem into Visits section body (legacy per-problem sections).
 */
export function formatAssessmentAndPlanBody(problem: SoapProblem): string {
  return formatCombinedAssessmentAndPlan([problem], '', '');
}

/** Flatten problems[] to assessment textarea text for the legacy detail editor. */
export function problemsToAssessmentText(problems: SoapProblem[] | null | undefined): string {
  if (!problems?.length) {
    return '';
  }
  return problems
    .map((p, i) => {
      const name = (p.name ?? '').trim() || `Problem ${i + 1}`;
      const body = flattenAssessmentNarrative(p.assessment);
      return `${i + 1}. ${name}:\n${body}`.trimEnd();
    })
    .join('\n\n')
    .trim();
}

/** Flatten problems[] to plan textarea text for the legacy detail editor. */
export function problemsToPlanText(problems: SoapProblem[] | null | undefined): string {
  if (!problems?.length) {
    return '';
  }
  return problems
    .map((p, i) => {
      const name = (p.name ?? '').trim() || `Problem ${i + 1}`;
      const items = flattenPlanItems(p.plan);
      const body = items.map((item) => `• ${item}`).join('\n');
      return `${i + 1}. ${name}:\n${body}`.trimEnd();
    })
    .join('\n\n')
    .trim();
}

/**
 * Best-effort parse of legacy editor assessment/plan textareas back into problems[].
 * Pairs by problem index/heading.
 */
export function sectionTextsToProblems(assessmentText: string, planText: string): SoapProblem[] {
  const aBlocks = splitNumberedBlocks(assessmentText);
  const pBlocks = splitNumberedBlocks(planText);
  const out: SoapProblem[] = [];
  const byIndex = new Map<number, SoapProblem>();

  aBlocks.forEach((b, i) => {
    const index = b.number || i + 1;
    const problem: SoapProblem = {
      index,
      name: b.name || `Problem ${index}`,
      assessment: flattenAssessmentNarrative(b.body),
      plan: [],
    };
    out.push(problem);
    byIndex.set(index, problem);
  });

  pBlocks.forEach((b, i) => {
    const index = b.number || i + 1;
    let match = byIndex.get(index);
    if (!match) {
      const norm = normalizeName(b.name);
      match = out.find((p) => normalizeName(p.name) === norm);
    }
    const items = flattenPlanItems(
      b.body
        .split('\n')
        .map((l) => l.replace(/^[•\-*]\s+/, '').trim())
        .filter(Boolean)
    );
    if (match) {
      match.plan = [...(match.plan ?? []), ...items];
    } else {
      out.push({
        index,
        name: b.name || `Problem ${index}`,
        assessment: '',
        plan: items,
      });
    }
  });

  return out.map((p, i) => ({ ...p, index: i + 1 }));
}

/**
 * Inverse of {@link formatCombinedAssessmentAndPlan}: split the Visits editor
 * text back into an overview paragraph plus per-problem assessment/plan.
 */
export function parseCombinedAssessmentAndPlan(
  text: string,
  originalProblems: SoapProblem[] | null | undefined
): { summary: string; problems: SoapProblem[] } {
  const raw = (text ?? '').replace(/\r/g, '').trim();
  if (!raw) {
    return { summary: '', problems: [] };
  }

  const original = originalProblems ?? [];
  const nameSet = new Map(
    original
      .map((p) => (p.name ?? '').trim())
      .filter(Boolean)
      .map((n) => [normalizeName(n), n] as const)
  );

  const lines = raw.split('\n');
  const foundOriginalHeading = lines.some((line) => {
    const key = normalizeName(line.trim().replace(/:$/, ''));
    return !!key && nameSet.has(key);
  });

  const headingFor = (line: string): string | null => {
    const t = line.trim().replace(/:$/, '');
    if (!t || /^plan$/i.test(t) || /^[•\-*]\s+/.test(line.trim())) {
      return null;
    }
    const key = normalizeName(t);
    const orig = nameSet.get(key);
    if (orig) {
      return orig;
    }
    if (foundOriginalHeading) {
      return null;
    }
    if (/[.?!]$/.test(t) || t.length > 80) {
      return null;
    }
    const words = t.split(/\s+/).filter(Boolean).length;
    if (words < 1 || words > 12) {
      return null;
    }
    return t;
  };

  const blocks: { name: string; body: string[] }[] = [];
  let preamble: string[] = [];
  let current: { name: string; body: string[] } | null = null;

  for (const line of lines) {
    const heading = headingFor(line);
    if (heading) {
      current = { name: heading, body: [] };
      blocks.push(current);
      continue;
    }
    if (current) {
      current.body.push(line);
    } else {
      preamble.push(line);
    }
  }

  const problems: SoapProblem[] = blocks.map((block, i) => {
    const bodyLines = block.body;
    let planAt = bodyLines.findIndex((l) => /^\s*plan:\s*$/i.test(l.trim()));
    if (planAt < 0) {
      planAt = bodyLines.findIndex((l) => /^\s*plan:\s+\S/i.test(l.trim()));
    }
    let assessmentRaw = '';
    let planRaw: string[] = [];
    if (planAt >= 0) {
      assessmentRaw = bodyLines.slice(0, planAt).join('\n');
      const planLine = bodyLines[planAt]?.trim() ?? '';
      const inline = planLine.replace(/^plan:\s*/i, '').trim();
      planRaw = [
        ...(inline ? [inline] : []),
        ...bodyLines.slice(planAt + 1),
      ];
    } else {
      assessmentRaw = bodyLines.join('\n');
    }
    const orig = original.find((p) => normalizeName(p.name) === normalizeName(block.name));
    return {
      index: orig?.index ?? i + 1,
      name: block.name,
      assessment: flattenAssessmentNarrative(assessmentRaw),
      plan: flattenPlanItems(
        planRaw
          .map((l) => l.replace(/^[•\-*]\s+/, '').trim())
          .filter(Boolean)
      ),
    };
  });

  return {
    summary: preamble.join('\n').trim(),
    problems: problems.map((p, i) => ({ ...p, index: i + 1 })),
  };
}

function splitNumberedBlocks(text: string): { number: number; name: string; body: string }[] {
  const raw = (text ?? '').replace(/\r/g, '').trim();
  if (!raw) {
    return [];
  }
  const lines = raw.split('\n');
  const blocks: { number: number; name: string; body: string }[] = [];
  let current: { number: number; name: string; body: string } | null = null;

  for (const line of lines) {
    const m = line.trim().match(/^(\d{1,2})\.\s+(.+?)(?::\s*)?$/);
    if (m && !/[.?!]$/.test(m[2]) && m[2].split(/\s+/).length <= 12) {
      current = { number: parseInt(m[1], 10), name: m[2].replace(/:$/, '').trim(), body: '' };
      blocks.push(current);
      continue;
    }
    if (current) {
      current.body += (current.body ? '\n' : '') + line;
    }
  }

  if (!blocks.length && raw) {
    blocks.push({ number: 1, name: 'Problem', body: raw });
  }
  return blocks;
}

function normalizeName(name: string): string {
  return (name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function stripBullet(line: string): string {
  return line.replace(/^[•\-*]\s+/, '').replace(/^\d{1,2}\.\s+/, '').trim();
}

function normalizeLabelKey(s: string): string {
  return s
    .toLowerCase()
    .replace(/[–—]/g, '-')
    .replace(/\//g, ' ')
    .replace(/[()]/g, ' ')
    .replace(/:+$/, '')
    .replace(/[^a-z0-9\-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function stripKnownLabel(
  line: string,
  labels: Set<string>
): { skip: boolean; content: string; labelKey: string | null } {
  const m = line.match(/^([A-Za-z][A-Za-z0-9\s/\-()]{0,44}):\s*(.*)$/);
  if (m) {
    const key = normalizeLabelKey(m[1]);
    if (labels.has(key)) {
      const content = (m[2] || '').trim();
      if (!content) {
        return { skip: true, content: '', labelKey: key };
      }
      return { skip: false, content, labelKey: key };
    }
    return { skip: false, content: line, labelKey: null };
  }
  const key = normalizeLabelKey(line);
  if (labels.has(key)) {
    return { skip: true, content: '', labelKey: key };
  }
  return { skip: false, content: line, labelKey: null };
}
